import React, { useRef, useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, ArrowRight, Loader2, Clock, RotateCcw, Hand } from "lucide-react";
import api, { apiErr } from "../lib/api";
import PackOpening from "./PackOpening";
import { fmtCountdown } from "../lib/helpers";

const reduced =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const PENDING_KEY = "luma_pending_open";
const THRESHOLD = 0.75;

function newKey() {
  return (crypto?.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`;
}

export default function OpenPackFlow({ set, state, imagesEnabled, recoverKey, onClose, onOpened }) {
  const keyRef = useRef(recoverKey || newKey());
  const sentRef = useRef(false);
  const [phase, setPhase] = useState(recoverKey ? "loading" : "closed"); // closed|loading|reveal|nocredit|error
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const zoneRef = useRef(null);
  const startXRef = useRef(0);

  const clearPending = () => localStorage.removeItem(PENDING_KEY);

  const sendOpen = useCallback(async () => {
    if (sentRef.current) return;
    sentRef.current = true;
    setPhase("loading");
    try {
      localStorage.setItem(PENDING_KEY, JSON.stringify({ key: keyRef.current, set_id: set.set_id, set_name: set.name }));
    } catch (e) { /* ignore */ }
    try {
      const { data } = await api.post("/open", { set_id: set.set_id, idempotency_key: keyRef.current });
      setResult(data);
      setPhase("reveal");
      onOpened?.(data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        clearPending();
        setPhase("nocredit");
      } else {
        // keep pending so retry reuses the same idempotency key (no double debit)
        sentRef.current = false;
        setPhase("error");
      }
    }
  }, [set, onOpened]);

  // auto-recover an interrupted opening after a page refresh
  useEffect(() => {
    if (recoverKey) sendOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const completeTear = useCallback(() => {
    setProgress(1);
    setDragging(false);
    setTimeout(sendOpen, reduced ? 0 : 420);
  }, [sendOpen]);

  // ---- gesture ----
  const onPointerDown = (e) => {
    if (phase !== "closed") return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    startXRef.current = e.clientX;
    setDragging(true);
  };
  const onPointerMove = (e) => {
    if (!dragging || phase !== "closed") return;
    e.preventDefault();
    const w = zoneRef.current?.offsetWidth || 1;
    const dx = Math.abs(e.clientX - startXRef.current);
    setProgress(Math.min(1, dx / w));
  };
  const onPointerUp = () => {
    if (phase !== "closed") return;
    setDragging(false);
    if (progress >= THRESHOLD) completeTear();
    else setProgress(0);
  };

  const resetReopen = () => {
    keyRef.current = newKey();
    sentRef.current = false;
    setResult(null);
    setProgress(0);
    setPhase("closed");
  };

  const handleClose = () => {
    clearPending();
    onClose();
  };

  if (phase === "reveal" && result) {
    const canReopen = (state?.ordinary_remaining || 0) > 0 || (state?.bonus_balance || 0) > 0;
    return (
      <PackOpening
        result={result}
        setName={set.name.split("[")[0].trim()}
        imagesEnabled={imagesEnabled}
        canReopen={canReopen}
        onClose={handleClose}
        onReopen={resetReopen}
      />
    );
  }

  const tearY = -progress * 120;

  return (
    <div className="fixed inset-0 z-50 bg-[#0b1324]/85 backdrop-blur-sm flex flex-col items-center justify-center px-6" data-testid="open-pack-flow">
      <button onClick={handleClose} data-testid="flow-close-btn" className="absolute top-4 right-4 pill-soft w-10 h-10 flex items-center justify-center">
        <X size={18} />
      </button>

      <div className="text-center mb-5">
        <div className="text-[11px] font-bold uppercase tracking-widest text-white/60">Apri la bustina</div>
        <h2 className="font-display font-bold text-xl text-white mt-0.5">{set.name.split("[")[0].trim()}</h2>
      </div>

      <AnimatePresence mode="wait">
        {phase === "closed" && (
          <motion.div key="closed" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex flex-col items-center w-full max-w-xs">
            {/* Pack */}
            <div className="relative w-52 aspect-[3/4.3] select-none" style={{ touchAction: "none" }}>
              {/* body */}
              <div className="absolute inset-0 rounded-[1.4rem] overflow-hidden shadow-[0_25px_50px_-18px_rgba(124,58,237,0.7)]"
                   style={{ background: "linear-gradient(155deg,#8b5cf6 0%,#ec4899 55%,#38bdf8 100%)" }}>
                <div className="absolute inset-0 foil-sheen" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-3 text-center">
                  <Sparkles size={30} className="mb-2 opacity-90" />
                  <div className="font-display font-extrabold text-3xl drop-shadow">{set.code}</div>
                  <div className="text-[11px] font-bold mt-1 opacity-90 line-clamp-2">{set.name.split("[")[0]}</div>
                </div>
                <div className="absolute bottom-2 inset-x-0 text-center text-white/90 font-display font-bold text-sm tracking-widest">LUMACOFONO</div>
              </div>

              {/* tear strip (top) */}
              <motion.div
                className="absolute -top-1 left-0 right-0 h-[22%] rounded-t-[1.4rem] overflow-hidden"
                animate={{ y: `${tearY}%`, opacity: progress > 0.9 ? 0 : 1, rotate: progress * 4 }}
                transition={{ type: dragging ? false : "spring", stiffness: 300, damping: 26 }}
                style={{ background: "linear-gradient(155deg,#7c3aed,#db2777)" }}
              >
                <div className="absolute inset-0 foil-sheen" />
                <div className="absolute bottom-0 left-0 right-0 h-1.5 border-b-[3px] border-dashed border-white/70" />
              </motion.div>

              {/* progress-driven peel light */}
              {progress > 0 && progress < 1 && (
                <div className="absolute left-0 right-0 h-0.5 bg-white/80 shadow-[0_0_10px_2px_rgba(255,255,255,0.8)]" style={{ top: `${22 - progress * 4}%` }} />
              )}

              {/* drag zone across the top edge */}
              <div
                ref={zoneRef}
                data-testid="pack-tear-zone"
                className="absolute -top-3 left-0 right-0 h-16 cursor-grab active:cursor-grabbing"
                style={{ touchAction: "none" }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
              >
                <motion.div
                  className="absolute top-1 left-1/2 -translate-x-1/2 flex items-center gap-1.5 text-white/90 bg-black/25 rounded-full px-3 py-1 text-xs font-bold whitespace-nowrap pointer-events-none"
                  animate={{ opacity: dragging ? 0 : [0.6, 1, 0.6], x: ["-60%", "-40%", "-60%"] }}
                  transition={{ duration: 1.6, repeat: Infinity }}
                >
                  Scorri per aprire <ArrowRight size={14} />
                </motion.div>
              </div>
            </div>

            {/* progress bar */}
            <div className="w-full mt-6 h-2 rounded-full bg-white/15 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-300 to-pink-400 transition-[width] duration-75" style={{ width: `${progress * 100}%` }} />
            </div>
            <p className="text-white/60 text-xs font-semibold mt-2 flex items-center gap-1.5">
              <Hand size={13} /> Trascina lungo il bordo superiore fino in fondo
            </p>

            {/* accessible fallback */}
            <button onClick={completeTear} data-testid="open-without-swipe-btn" className="pill-primary w-full mt-5 py-3.5 text-base">
              Apri senza scorrere
            </button>
          </motion.div>
        )}

        {phase === "loading" && (
          <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center text-white">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
              <Loader2 size={44} />
            </motion.div>
            <p className="mt-4 font-bold">Apertura in corso…</p>
            <p className="text-white/60 text-sm">Stiamo salvando la tua bustina</p>
          </motion.div>
        )}

        {phase === "nocredit" && (
          <NoCredit key="nocredit" state={state} onClose={handleClose} />
        )}

        {phase === "error" && (
          <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center text-white text-center max-w-xs">
            <p className="font-bold text-lg">Qualcosa è andato storto</p>
            <p className="text-white/60 text-sm mt-1">Nessun credito è stato consumato in modo errato: puoi riprovare in sicurezza.</p>
            <div className="flex gap-2.5 mt-5 w-full">
              <button onClick={handleClose} className="pill-soft flex-1 py-3 text-sm">Chiudi</button>
              <button onClick={sendOpen} data-testid="retry-open-btn" className="pill-primary flex-1 py-3 text-sm flex items-center justify-center gap-2">
                <RotateCcw size={16} /> Riprova
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function NoCredit({ state, onClose }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const end = state?.window_end ? new Date(state.window_end).getTime() : null;
  const ms = end ? end - now : 0;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center text-white text-center max-w-xs">
      <Clock size={40} className="text-amber-300" />
      <p className="font-bold text-lg mt-3">Aperture esaurite</p>
      <p className="text-white/60 text-sm mt-1">Nessun credito disponibile. Attendi il ripristino o usa una bustina bonus.</p>
      <div className="font-mono font-bold text-2xl mt-3 tabular-nums" data-testid="nocredit-countdown">
        {end && ms > 0 ? fmtCountdown(ms) : "Pronto"}
      </div>
      <button onClick={onClose} className="pill-soft w-full mt-5 py-3 text-sm">Chiudi</button>
    </motion.div>
  );
}
